/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
import * as React from 'react';

import {BreakdownPreviewProps, BreakdownType} from 'common/onboarding/immersive-onboarding/types';
import {PROPERTY_BREAKDOWN_MAP} from 'common/onboarding/immersive-onboarding/constants';

import {CardSnabbdom} from '../chart-card';

const OVERALL = [
  {
    count: 45244,
    avg_time: null,
    avg_time_from_start: null,
    event: `[ui-perf] CurrentProjectClient query fired`,
    goal: `[ui-perf] CurrentProjectClient query fired`,
    step_label: `[ui-perf] CurrentProjectClient query fired`,
    overall_conv_ratio: 1,
    step_conv_ratio: 1,
  },
  {
    count: 24994,
    avg_time: 175756,
    avg_time_from_start: 175756,
    event: `[ui-perf] Initial Dashboard Cards`,
    goal: `[ui-perf] Initial Dashboard Cards`,
    step_label: `[ui-perf] Initial Dashboard Cards`,
    overall_conv_ratio: 0.5524268411281054,
    step_conv_ratio: 0.5524268411281054,
    p_value: {insufficient_samples: false, p: 1, confidence: 0, is_interesting: false},
  },
  {
    count: 21760,
    avg_time: 84369,
    avg_time_from_start: 238643,
    event: `[ui-perf] Initial Dashboard Cards`,
    goal: `[ui-perf] Initial Dashboard Cards`,
    step_label: `[ui-perf] Initial Dashboard Cards`,
    overall_conv_ratio: 0.48094774997789763,
    step_conv_ratio: 0.8706089461470753,
    p_value: {insufficient_samples: false, p: 1, confidence: 0, is_interesting: false},
  },
  {
    count: 15159,
    avg_time: 238126,
    avg_time_from_start: 448583,
    event: `[ui-perf] Properties Menu Loaded`,
    goal: `[ui-perf] Properties Menu Loaded`,
    step_label: `[ui-perf] Properties Menu Loaded`,
    overall_conv_ratio: 0.33504995137476795,
    step_conv_ratio: 0.6966452205882353,
    p_value: {insufficient_samples: false, p: 1, confidence: 0, is_interesting: false},
  },
];

const SERIES_A = [
  {
    count: 44491,
    avg_time: null,
    avg_time_from_start: null,
    overall_conv_ratio: 1,
    step_conv_ratio: 1,
  },
  {
    count: 24781,
    avg_time: 175495,
    avg_time_from_start: 175495,
    overall_conv_ratio: 0.5569890539659706,
    step_conv_ratio: 0.5569890539659706,
    p_value: {insufficient_samples: false, p: 0, confidence: 1, is_interesting: true},
  },
  {
    count: 21591,
    avg_time: 83978,
    avg_time_from_start: 238132,
    overall_conv_ratio: 0.48528915960531344,
    step_conv_ratio: 0.8712723457487591,
    p_value: {insufficient_samples: false, p: 0, confidence: 1, is_interesting: true},
  },
  {
    count: 15131,
    avg_time: 237957,
    avg_time_from_start: 447856,
    overall_conv_ratio: 0.34009125441100446,
    step_conv_ratio: 0.7008012597841693,
    p_value: {insufficient_samples: false, p: 0, confidence: 1, is_interesting: true},
  },
];

const SERIES_B = [
  {
    count: 1519,
    avg_time: null,
    avg_time_from_start: null,
    overall_conv_ratio: 1,
    step_conv_ratio: 1,
  },
  {
    count: 948,
    avg_time: 63693,
    avg_time_from_start: 63693,
    overall_conv_ratio: 0.6240947992100065,
    step_conv_ratio: 0.6240947992100065,
    p_value: {
      insufficient_samples: false,
      p: 2.264178178279508e-9,
      confidence: 0.9999999977358218,
      is_interesting: true,
    },
  },
  {
    count: 884,
    avg_time: 39736,
    avg_time_from_start: 88938,
    overall_conv_ratio: 0.5819618169848585,
    step_conv_ratio: 0.9324894514767933,
    p_value: {
      insufficient_samples: false,
      p: 2.264178178279508e-9,
      confidence: 0.9999999977358218,
      is_interesting: true,
    },
  },
  {
    count: 617,
    avg_time: 94761,
    avg_time_from_start: 147230,
    overall_conv_ratio: 0.40618828176431865,
    step_conv_ratio: 0.6979638009049773,
    p_value: {
      insufficient_samples: false,
      p: 2.264178178279508e-9,
      confidence: 0.9999999977358218,
      is_interesting: true,
    },
  },
];

const replaceEventName = (event, data) => {
  data[`event`] = event;
  data[`goal`] = event;
  data[`step_label`] = event;

  return data;
};

const getData = (events, property) => {
  const data = {
    $overall: events.map((event, index) => {
      return replaceEventName(event, OVERALL[index]);
    }),
  };

  data[PROPERTY_BREAKDOWN_MAP[property][0]] = events.map((event, index) => {
    return replaceEventName(event, SERIES_A[index]);
  });

  data[PROPERTY_BREAKDOWN_MAP[property][1]] = events.map((event, index) => {
    return replaceEventName(event, SERIES_B[index]);
  });

  return {
    '2023-10-22': data,
  };
};

const props = (events: string[], property: BreakdownType) => ({
  cardTitle: `Funnel Breakdown Sample`,
  projectId: ``,
  chartOptions: {type: `funnel-steps`},
  bookmark: {
    metadata: {
      chart_type: `chart`,
      num_days: 30,
      segmented: false,
      has_event_comparison: false,
      selected_step: null,
      trend_view_type: `conversion`,
      frequency_representation_type: `Conversion`,
      time_buckets: {min: 0, max: 2592000, offset: 0, bucket_size: 172800, is_drilled_down: false},
      frequency_buckets: {min: 1, max: 20, offset: 0, bucket_size: 1},
      name: `Funnel Breakdown Sample`,
      description: ``,
      select_mode: `positional`,
      selected_segments: [],
      time_comparison_label: null,
      ttc_value_representation: `relative`,
      frequency_value_representation: `relative`,
    },
    dashboard: {},
    params: {
      stat_sig_control: [],
      chart_type: `chart`,
      count_type: `unique`,
      date_range: {type: `in the last`, from_date: {unit: `day`, value: 30}, to_date: `$now`},
      time_comparison: null,
      exclusions: [],
      filter_by_cohort: `{}`,
      length: 30,
      length_unit: `day`,
      time_buckets: {min: 0, max: 2592000, offset: 0, bucket_size: 172800, is_drilled_down: false},
      frequency_buckets: {min: 1, max: 20, offset: 0, bucket_size: 1},
      trend_unit: `day`,
      trend_count_type: `linear`,
      funnel_order: `loose`,
      version: 2,
      steps: events.map((ev) => ({
        step_label: ev,
        event: ev,
        bool_op: `and`,
        property_filter_params_list: [],
      })),
      savedLegend: [],
      selectedTableColumns: {
        'stat-sig': true,
        'conv-prev-step': true,
        'conv-first-step': false,
        count: true,
        'time-prev-step': true,
        'time-first-step': false,
      },
      ttcVizType: `Bar`,
      frequencyVizType: `Bar`,
      stepsVizType: `Steps`,
      ttcAggregateType: `Distribution`,
      frequencyAggregateType: `Distribution`,
      liftComparison: null,
      eventIndex: null,
      trendType: `conversion`,
      frequencyType: `Conversion`,
      ttcValueRepresentation: `relative`,
      frequencyValueRepresentation: `relative`,
      sorting: {
        sortBy: `value`,
        sortOrder: `desc`,
        valueField: `step-0`,
        colSortAttrs: [{sortBy: `value`, sortOrder: `desc`, valueField: `step-0`}],
      },
      action_mode: `last_touch`,
      group_by: [],
    },
  },
  chartParams: {
    computed_at: `2023-11-21T14:29:51.809133+00:00`,
    data: getData(events, property),
    meta: {
      dates: [`2023-10-22`],
      property_values: PROPERTY_BREAKDOWN_MAP[property],
      min_sampling_factor: 1,
      group_by_metadata: [
        {
          bookmark: {
            event: {
              resourceType: `event`,
              propertyDefaultType: `string`,
              propertyName: property,
              propertyType: `string`,
            },
          },
        },
      ],
    },
    min_sampling_factor: 1,
  },
  dataGroups: null,
});

export const FunnelBreakdownPreview = ({events, property}: BreakdownPreviewProps) => {
  //@ts-ignore
  return <CardSnabbdom params={props(events, property)} />;
};
